'use client';

import {
  ColumnFiltersState, createColumnHelper, getFilteredRowModel, PaginationState
} from '@tanstack/table-core';
import clsx from 'clsx';
import { useCallback, useMemo, useState } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { Button } from '@/components/button/button';
import { useFlag } from '@/components/flags/provider';
import { Loader } from '@/components/loader/loader';
import Modal from '@/components/modal/modal';
import { NativeTable } from '@/components/nativeTable/nativeTable';
import { useNativeTable } from '@/components/nativeTable/useNativeTable';
import { buildColumnClassString, ColumnModEnums } from '@/components/table/table';
import { Heading } from '@/components/typography/heading';
import { showAuditLogs } from '@/flags';
import { useAuditLogsQuery } from '@/hooks/queries/useAuditLogs';
import { AuditLogExposed } from '@/types/auditLogs';

import pageStyles from './page.module.scss';

export const dynamic = 'force-dynamic';


const columnHelper = createColumnHelper<AuditLogExposed>();

export default function AuditLogsPage() {
  const canViewAuditLogs = useFlag( showAuditLogs );

  const [ columnFilters, setColumnFilters ] = useState<ColumnFiltersState>( [] );
  const [ pagination, setPagination ] = useState<PaginationState>( { pageIndex: 0, pageSize: 50 } );
  const [ selectedAuditLog, setSelectedAuditLog ] = useState<AuditLogExposed | null>( null );

  const { isPending, error, data } = useAuditLogsQuery(
    {
      limit: pagination.pageSize,
      offset: pagination.pageIndex * pagination.pageSize
    }
  );

  if ( error ) {
    console.error( `unable to get audit logs: ${ error }` );
    toast.error( 'unable to get audit logs' );
  }

  const auditLogs = data?.audit_logs ?? [];

  const columns = useMemo( () => [
    columnHelper.accessor( 'timestamp', {
      header: 'Timestamp',
      size: 180,
      cell: info => {
        const timestamp = info.getValue();
        return new Date( timestamp ).toLocaleString();
      },
      meta: {
        classStr: buildColumnClassString( { width: 15 } )
      }
    } ),
    columnHelper.accessor( 'action', {
      header: 'Action',
      size: 140,
      cell: info => info.getValue(),
      meta: {
        classStr: buildColumnClassString( { width: 12 } )
      }
    } ),
    columnHelper.accessor( 'status', {
      header: 'Status',
      size: 100,
      cell: info => {
        const status = info.getValue();
        return (
          <span className={ clsx( pageStyles.status, pageStyles[status.toLowerCase()] ) }>
            {status}
          </span>
        );
      },
      meta: {
        classStr: buildColumnClassString( { width: 8, styles: ColumnModEnums.CENTER } )
      }
    } ),
    columnHelper.accessor( 'actor_email', {
      header: 'Actor',
      size: 140,
      cell: info => info.getValue() || info.row.original.actor_username || '-',
      meta: {
        classStr: buildColumnClassString( { width: 12 } )
      }
    } ),
    columnHelper.accessor( 'resource_type', {
      header: 'Resource Type',
      size: 120,
      cell: info => info.getValue() || '-',
      meta: {
        classStr: buildColumnClassString( { width: 10 } )
      }
    } ),
    columnHelper.accessor( 'resource_id', {
      header: 'Resource ID',
      size: 140,
      cell: info => {
        const resourceId = info.getValue();
        if ( !resourceId ) return '-';
        return resourceId.length > 20 ? resourceId.slice( 0, 20 ) + '...' : resourceId;
      },
      meta: {
        classStr: buildColumnClassString( { width: 12 } )
      }
    } ),
    columnHelper.accessor( 'message', {
      header: 'Message',
      size: 200,
      cell: info => {
        const message = info.getValue();
        return message && message.length > 50
          ? message.slice( 0, 50 ) + '...'
          : message;
      },
      meta: {
        classStr: buildColumnClassString( { width: 18 } )
      }
    } )
  ], [] );

  const handleRowClick = useCallback( ( auditLog: AuditLogExposed ) => {
    const hasDetails = auditLog.details && Object.keys( auditLog.details ).length > 0;
    if ( !hasDetails ) return;
    setSelectedAuditLog( auditLog );
  }, [] );

  const tableState = useMemo( () => ( { columnFilters, pagination } ), [ columnFilters, pagination ] );

  const table = useNativeTable<AuditLogExposed>( {
    data: auditLogs,
    columns,
    state: tableState,
    onColumnFiltersChange: setColumnFilters,
    onPaginationChange: setPagination,
    getFilteredRowModel: getFilteredRowModel(),
    pageCount: data ? Math.ceil( data.total / pagination.pageSize ) : -1,
    rowCount: data?.total ?? 0,
    manualPagination: true,
    enableSorting: false
  } );
  if ( !canViewAuditLogs ) {
    return (
      <div className="page">
        <div className={ clsx( 'layout__row', 'pad' ) }>
          <Heading>Unauthorized</Heading>
          <p>You do not have permission to view this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <ToastContainer />
      <div className={ clsx( 'layout__row', 'pad' ) }>
        <Heading>Audit Logs</Heading>
      </div>

      {isPending ? (
        <Loader />
      ) : (
        <NativeTable table={ table } pagination="server" onRowClick={ handleRowClick } />
      )}

      { selectedAuditLog && (
        <Modal className={ pageStyles.audit_modal }>
          <div className={ pageStyles.modal_content }>
            <div className={ pageStyles.modal_header }>
              <Heading as="h2">Audit Log: { selectedAuditLog.action }</Heading>
              <button
                onClick={ () => setSelectedAuditLog( null ) }
                className={ pageStyles.close_button }
              >
                ×
              </button>
            </div>
            <div className={ pageStyles.modal_fields }>
              { Object.entries( selectedAuditLog ).map( ( [ key, value ] ) => {
                if ( value === null || value === undefined ) return null;
                if ( key === 'details' ) return null; // Skip details, will render separately

                return (
                  <div className={ pageStyles.field_item } key={ key }>
                    <fieldset>
                      <legend className={ pageStyles.field_key }>{ key }</legend>
                      <p className={ pageStyles.field_value }>
                        { typeof value === 'object' ? JSON.stringify( value, null, 2 ) : String( value ) }
                      </p>
                    </fieldset>
                  </div>
                );
              } ) }
            </div>
            { selectedAuditLog.details && Object.keys( selectedAuditLog.details ).length > 0 && (
              <>
                <Heading as="h3" size="md" className={ pageStyles.details_heading }>Details</Heading>
                <div className={ pageStyles.modal_fields }>
                  { Object.entries( selectedAuditLog.details ).map( ( [ key, value ] ) => (
                    <div className={ pageStyles.field_item } key={ `details.${ key }` }>
                      <fieldset>
                        <legend className={ pageStyles.field_key }>{ key }</legend>
                        <p className={ pageStyles.field_value }>
                          { value !== null && value !== undefined
                            ? typeof value === 'object'
                              ? JSON.stringify( value, null, 2 )
                              : String( value )
                            : '-' }
                        </p>
                      </fieldset>
                    </div>
                  ) ) }
                </div>
              </>
            ) }
            <div className={ pageStyles.modal_footer }>
              <Button variant='primary' onClick={ () => setSelectedAuditLog( null ) }>
                Close
              </Button>
            </div>
          </div>
        </Modal>
      ) }
    </div>
  );
}
